import { useState } from 'react';
import { api } from '@/api/client';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  Modal,
  OkNote,
  Select,
  StatusPill,
  Table,
  errorMessage,
} from '@/components/ui';
import { formatDate, formatMoney } from '@/format';
import {
  ACCOUNT_TYPE_LABELS,
  useAccounts,
  useBankAccounts,
  useCashBoxes,
  useCloseReadiness,
  useExpenseCategories,
  useFinanceMutation,
  useFinanceSettings,
  usePeriods,
  type PeriodRow,
} from '@/hooks/finance';
import { useBuyers, BUYER_KIND_LABELS } from '@/hooks/sales';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';

type Tab = 'chart' | 'money' | 'categories' | 'buyers' | 'periods' | 'settings';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Financials › Setup.
 *
 * Six things that are decided once and then left alone, behind one tab strip
 * rather than six sidebar entries: the chart of accounts, where money is kept,
 * what it can be spent on, who buys, the accounting calendar, and the settings.
 *
 * Closing a month lists everything in the way at once rather than failing on the
 * first problem — being told five times in a row, one reason each, is worse than
 * one list.
 */
export function FinanceSetup() {
  const can = useAuth((s) => s.can);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [tab, setTab] = useState<Tab>('chart');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState<PeriodRow | null>(null);
  const [reopening, setReopening] = useState<PeriodRow | null>(null);
  const [reason, setReason] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({
    code: '',
    nameEn: '',
    parentId: '',
    requiresFarm: false,
  });

  const accounts = useAccounts();
  const cashBoxes = useCashBoxes(farmIds);
  const banks = useBankAccounts(farmIds);
  const categories = useExpenseCategories(true);
  const buyers = useBuyers();
  const periods = usePeriods();
  const settings = useFinanceSettings();
  const readiness = useCloseReadiness(closing?.id ?? null);
  const mutate = useFinanceMutation<unknown>();

  const groups = (accounts.data?.items ?? []).filter((a) => !a.isPostable);

  async function run(action: () => Promise<unknown>, ok: string, fallback: string) {
    setError('');
    setNote('');
    try {
      await mutate.mutateAsync(action);
      setNote(ok);
      return true;
    } catch (e) {
      setError(errorMessage(e, fallback));
      return false;
    }
  }

  const tabs: Array<{ id: Tab; label: string; show: boolean }> = [
    { id: 'chart', label: 'Chart of accounts', show: can('fin.account.read') },
    { id: 'money', label: 'Cash and bank', show: can('fin.bank.read') },
    { id: 'categories', label: 'What money goes on', show: can('fin.expense.read') },
    { id: 'buyers', label: 'Buyers and brokers', show: can('fin.broker.read') },
    { id: 'periods', label: 'Months and years', show: can('fin.period.read') },
    { id: 'settings', label: 'Settings', show: can('fin.account.read') },
  ];
  const visible = tabs.filter((t) => t.show);

  return (
    <div className="stack">
      {/* The same control Farm Manager and Attendance use for their own view
          switches, rather than a new one that would have to be styled and made
          keyboard-reachable all over again. */}
      <div className="viewtoggle" role="tablist" aria-label="Finance setup">
        {visible.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`viewtoggle__btn${tab === t.id ? ' viewtoggle__btn--on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {note && <OkNote message={note} />}
      {error && <ErrorNote message={error} />}

      {tab === 'chart' && (
        <section className="panel">
          <div className="formpanel__head">
            <div>
              <h2>Chart of accounts</h2>
              <p className="muted">
                One chart for the whole organisation. A farm is a dimension on each
                entry, not a copy of the chart — which is what lets a per-farm report
                be a grouping rather than a guess.
              </p>
            </div>
            {can('fin.account.create') && (
              <button
                type="button"
                className="btn btn--sm btn--primary"
                onClick={() => setAddingAccount(true)}
              >
                Add an account
              </button>
            )}
          </div>
          <Table
            count={accounts.data?.items.length ?? 0}
            empty="The chart has not been seeded."
            head={
              <tr>
                <th>Code</th>
                <th>Account</th>
                <th>Kind</th>
                <th>Takes entries</th>
                <th>Needs</th>
                <th className="num">Balance</th>
              </tr>
            }
          >
            {(accounts.data?.items ?? []).map((a) => (
              <tr key={a.id} className={a.isPostable ? undefined : 'row--group'}>
                <td className="mono">{a.code}</td>
                <td style={{ paddingLeft: a.parentId ? '1.5rem' : undefined }}>
                  {a.isPostable ? a.nameEn : <strong>{a.nameEn}</strong>}
                  {!a.isActive && <span className="muted"> — closed</span>}
                </td>
                <td>{ACCOUNT_TYPE_LABELS[a.type] ?? a.type}</td>
                <td>{a.isPostable ? 'Yes' : <span className="muted">group total</span>}</td>
                <td>
                  {a.subledger ? `a ${a.subledger.toLowerCase()}` : ''}
                  {a.requiresFarm ? (a.subledger ? ', a farm' : 'a farm') : ''}
                  {!a.subledger && !a.requiresFarm && <span className="muted">—</span>}
                </td>
                <td className="num">{formatMoney(a.closingBalance)}</td>
              </tr>
            ))}
          </Table>
        </section>
      )}

      {tab === 'money' && (
        <>
          <section className="panel">
            <h2>Cash boxes</h2>
            <p className="muted">
              A cash box is a party on "Cash in hand", not an account of its own — so
              adding one never changes the shape of the chart.
            </p>
            <Table
              count={cashBoxes.data?.items.length ?? 0}
              empty="No cash boxes yet."
              head={
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Where</th>
                  <th className="num">Cash in hand</th>
                </tr>
              }
            >
              {(cashBoxes.data?.items ?? []).map((b) => (
                <tr key={b.id}>
                  <td className="mono">{b.code}</td>
                  <td>{b.nameEn}</td>
                  <td>{b.farmName}</td>
                  <td className="num">{formatMoney(b.closingBalance)}</td>
                </tr>
              ))}
            </Table>
          </section>

          <section className="panel">
            <h2>Bank accounts and wallets</h2>
            <p className="muted">
              A mobile wallet is a bank account whose bank is the provider, so its float
              shows up and can be reconciled like any other.
            </p>
            <Table
              count={banks.data?.items.length ?? 0}
              empty="No bank accounts yet."
              head={
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Bank</th>
                  <th>Account number</th>
                  <th className="num">Balance</th>
                </tr>
              }
            >
              {(banks.data?.items ?? []).map((b) => (
                <tr key={b.id}>
                  <td className="mono">{b.code}</td>
                  <td>{b.nameEn}</td>
                  <td>{b.bankLabel ?? ''}</td>
                  <td className="mono">
                    {/* Absent rather than blank for anyone without fin.bankdetail.read:
                        the server never sent it. */}
                    {b.accountNumber ?? <span className="muted">hidden</span>}
                  </td>
                  <td className="num">{formatMoney(b.closingBalance)}</td>
                </tr>
              ))}
            </Table>
          </section>
        </>
      )}

      {tab === 'categories' && (
        <section className="panel">
          <h2>What money goes on</h2>
          <p className="muted">
            Each one points at the account it posts to. The three marked as stock are
            refused on an expense and sent to Stock IN — buying feed is not a cost until
            it is eaten.
          </p>
          <Table
            count={categories.data?.items.length ?? 0}
            empty="No categories yet."
            head={
              <tr>
                <th>Code</th>
                <th>What for</th>
                <th>Posts to</th>
                <th>Goes into stock</th>
                <th>In use</th>
              </tr>
            }
          >
            {(categories.data?.items ?? []).map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.code}</td>
                <td>{c.nameEn}</td>
                <td>
                  {c.accountCode} — {c.accountName}
                </td>
                <td>{c.isStockPurchase ? 'Yes — use Stock IN' : 'No'}</td>
                <td>{c.isActive ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </Table>
        </section>
      )}

      {tab === 'buyers' && (
        <section className="panel">
          <h2>Buyers and brokers</h2>
          <p className="muted">
            One master with a kind, so a shop and a cash buyer can hold an account just
            as a beopari does.
          </p>
          <Table
            count={buyers.data?.items.length ?? 0}
            empty="No buyers yet."
            head={
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Kind</th>
                <th>Phone</th>
                <th className="num">Credit limit</th>
                <th className="num">Owes</th>
              </tr>
            }
          >
            {(buyers.data?.items ?? []).map((b) => (
              <tr key={b.id}>
                <td className="mono">{b.code}</td>
                <td>{b.nameEn}</td>
                <td>{BUYER_KIND_LABELS[b.kind] ?? b.kind}</td>
                <td>{b.contactNo ?? ''}</td>
                <td className="num">
                  {b.creditLimit ? formatMoney(b.creditLimit) : <span className="muted">none</span>}
                </td>
                <td className="num">
                  {formatMoney(b.closingBalance)}
                  {b.overLimit && <span className="field__problem"> over limit</span>}
                </td>
              </tr>
            ))}
          </Table>
        </section>
      )}

      {tab === 'periods' && (
        <section className="panel">
          <div className="formpanel__head">
            <div>
              <h2>Months and years</h2>
              <p className="muted">
                Closing a month does not change anything already written down — it stops
                anything new being written into it.
              </p>
            </div>
            {can('fin.period.post') && (
              <button
                type="button"
                className="btn btn--sm"
                disabled={mutate.isPending}
                onClick={() =>
                  void run(
                    () => api.post('/api/v1/fin/periods/open-year', {}),
                    'The next year is open.',
                    'That year could not be opened',
                  )
                }
              >
                Open the next year
              </button>
            )}
          </div>

          {(periods.data?.items ?? []).map((year) => (
            <div key={year.id}>
              <h3>
                {year.code}{' '}
                <StatusPill status={year.status === 'OPEN' ? 'DRAFT' : 'POSTED'} />
                {year.rolled && <span className="muted"> — rolled to retained earnings</span>}
              </h3>
              <Table
                count={year.periods.length}
                empty="No months."
                head={
                  <tr>
                    <th>Month</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Status</th>
                    <th />
                  </tr>
                }
              >
                {year.periods.map((p) => (
                  <tr key={p.id}>
                    <td>{p.label}</td>
                    <td>{formatDate(p.startsOn)}</td>
                    <td>{formatDate(p.endsOn)}</td>
                    <td>
                      <StatusPill status={p.status === 'OPEN' ? 'DRAFT' : 'POSTED'} />
                      {p.reopened && <span className="muted"> — reopened once</span>}
                    </td>
                    <td>
                      {p.status === 'OPEN' && can('fin.period.post') && (
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => setClosing(p)}
                        >
                          Close
                        </button>
                      )}
                      {p.status === 'CLOSED' && can('fin.period.reverse') && (
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          onClick={() => setReopening(p)}
                        >
                          Reopen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          ))}
        </section>
      )}

      {tab === 'settings' && settings.data && (
        <FormPanel
          title="Finance settings"
          intro="Set once, at the start. The fiscal start month cannot change after a year has been opened."
        >
          <FormGroup title="The year" columns={2}>
            <Select
              label="Fiscal year starts in"
              value={String(settings.data.fiscalYearStartMonth)}
              onChange={(v) =>
                void run(
                  () =>
                    api.patch('/api/v1/fin/settings', {
                      version: settings.data!.version,
                      fiscalYearStartMonth: Number(v),
                    }),
                  'Saved.',
                  'That could not be changed',
                )
              }
              options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
              disabled={!can('fin.account.update')}
              hint="July in Pakistan."
            />
          </FormGroup>

          <FormGroup title="Hand-offs from other modules" columns={2}>
            <Select
              label="Stock movements write to the ledger"
              value={settings.data.autoPostInventory ? 'yes' : 'no'}
              onChange={(v) =>
                void run(
                  () =>
                    api.patch('/api/v1/fin/settings', {
                      version: settings.data!.version,
                      autoPostInventory: v === 'yes',
                    }),
                  'Saved.',
                  'That could not be changed',
                )
              }
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No — hold them back for now' },
              ]}
              disabled={!can('fin.account.update')}
              hint="Turn off only while the item categories are still being mapped."
            />
            <Select
              label="Pay runs write to the ledger"
              value={settings.data.autoPostPayroll ? 'yes' : 'no'}
              onChange={(v) =>
                void run(
                  () =>
                    api.patch('/api/v1/fin/settings', {
                      version: settings.data!.version,
                      autoPostPayroll: v === 'yes',
                    }),
                  'Saved.',
                  'That could not be changed',
                )
              }
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No — hold them back for now' },
              ]}
              disabled={!can('fin.account.update')}
            />
          </FormGroup>
        </FormPanel>
      )}

      {addingAccount && (
        <Modal title="Add an account" onClose={() => setAddingAccount(false)}>
          <FormGroup title="The account" columns={2}>
            <Field
              label="Code"
              value={newAccount.code}
              onChange={(v) => setNewAccount((a) => ({ ...a, code: v }))}
              required
              mono
              hint="Its number in the chart, e.g. 5345."
            />
            <Field
              label="Name"
              value={newAccount.nameEn}
              onChange={(v) => setNewAccount((a) => ({ ...a, nameEn: v }))}
              required
            />
            <Select
              label="Under"
              value={newAccount.parentId}
              onChange={(v) => setNewAccount((a) => ({ ...a, parentId: v }))}
              options={groups.map((g) => ({ value: g.id, label: `${g.code} — ${g.nameEn}` }))}
              required
              hint="It takes the same side of the books as whatever it sits under."
            />
            <Select
              label="Every entry must name a farm"
              value={newAccount.requiresFarm ? 'yes' : 'no'}
              onChange={(v) => setNewAccount((a) => ({ ...a, requiresFarm: v === 'yes' }))}
              options={[
                { value: 'no', label: 'No' },
                { value: 'yes', label: 'Yes — it is a farm cost or a farm income' },
              ]}
            />
          </FormGroup>
          {error && <ErrorNote message={error} />}
          <FormActions onCancel={() => setAddingAccount(false)}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={mutate.isPending}
              onClick={() =>
                void run(
                  () => api.post('/api/v1/fin/accounts', newAccount),
                  'Account added.',
                  'That account could not be added',
                ).then((ok) => {
                  if (ok) {
                    setAddingAccount(false);
                    setNewAccount({ code: '', nameEn: '', parentId: '', requiresFarm: false });
                  }
                })
              }
            >
              Add account
            </button>
          </FormActions>
        </Modal>
      )}

      {closing && (
        <Modal title={`Close ${closing.label}`} onClose={() => setClosing(null)}>
          {readiness.isLoading && <p className="muted">Checking…</p>}
          {readiness.data && readiness.data.length === 0 && (
            <OkNote message="Nothing is in the way. Closing stops anything new being posted into this month." />
          )}
          {readiness.data && readiness.data.length > 0 && (
            <>
              <p>This month is not ready to close:</p>
              <ul>
                {readiness.data.map((b, i) => (
                  <li key={i}>{b.what}</li>
                ))}
              </ul>
            </>
          )}
          {error && <ErrorNote message={error} />}
          <FormActions onCancel={() => setClosing(null)}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={
                mutate.isPending || readiness.isLoading || (readiness.data?.length ?? 1) > 0
              }
              onClick={() =>
                void run(
                  () =>
                    api.post(`/api/v1/fin/periods/${closing.id}/close`, {
                      version: closing.version,
                    }),
                  `${closing.label} is closed.`,
                  'That month could not be closed',
                ).then((ok) => ok && setClosing(null))
              }
            >
              Close {closing.label}
            </button>
          </FormActions>
        </Modal>
      )}

      {reopening && (
        <Modal title={`Reopen ${reopening.label}`} onClose={() => setReopening(null)}>
          <p>
            Reopening a closed month is the Managing Director's, and it is recorded as a
            reversal so it shows up in the audit log beside every other undo.
          </p>
          <Field
            label="Why"
            value={reason}
            onChange={setReason}
            required
            placeholder="A supplier bill for September arrived in November"
          />
          {error && <ErrorNote message={error} />}
          <FormActions onCancel={() => setReopening(null)}>
            <button
              type="button"
              className="btn btn--danger"
              disabled={mutate.isPending || !reason.trim()}
              onClick={() =>
                void run(
                  () =>
                    api.post(`/api/v1/fin/periods/${reopening.id}/reopen`, {
                      version: reopening.version,
                      reason: reason.trim(),
                    }),
                  `${reopening.label} is open again.`,
                  'That month could not be reopened',
                ).then((ok) => {
                  if (ok) {
                    setReopening(null);
                    setReason('');
                  }
                })
              }
            >
              Reopen {reopening.label}
            </button>
          </FormActions>
        </Modal>
      )}
    </div>
  );
}
